<template>
   <!-- Withdrawal - step 1 - Are you sure you want to withdraw from this study? -->
   <Modal
      v-if="step === 1"
      id="study-withdraw-step-1"
      ref="modalStep1Ref"
      width="sm"
      :pill="{ text: $t('account.withdrawal.modal_label'), theme: Theme.Danger }"
      @close="endWithdrawalProcess"
   >
      <template #title>
         <h1 class="text-regular line-clamp-4 grow text-center text-xl text-black md:text-2xl">
            {{ $t("account.withdrawal.step1.heading", { studyName: props.studyName }) }}
         </h1>
      </template>

      <div>
         <p class="mb-8 text-center" data-cy="study-withdraw-step-1-modal-desc">
            {{ $t("account.withdrawal.step1.description") }}
         </p>
         <ul class="">
            <li class="mb-4 flex">
               <IconExclamationCircle class="mr-2 mt-1 shrink-0" :fill="`var(--alert-fg)`" width="20" height="20" />
               <span>{{ $t("account.withdrawal.step1.info1") }}</span>
            </li>
            <li class="mb-4 flex">
               <IconExclamationCircle class="mr-2 mt-1 shrink-0" :fill="`var(--alert-fg)`" width="20" height="20" />
               <i18n-t keypath="account.withdrawal.step1.info2" tag="span" scope="global">
                  <template v-slot:privacyPolicy>
                     <a href="/privacy" class="underline" target="_blank">{{
                        $t("account.withdrawal.step1.privacy_policy")
                     }}</a>
                  </template>
               </i18n-t>
            </li>
         </ul>
         <p class="text-sm">{{ $t("account.withdrawal.step1.bottom_text") }}</p>
      </div>
      <template #action-btns>
         <DefaultButton
            data-cy="test-modal-cancel-btn"
            :color="Theme.Grey"
            size="sm"
            class="w-full"
            @click="endWithdrawalProcess"
            >{{ $t("system.button_cancel") }}</DefaultButton
         >
         <DefaultButton
            data-cy="test-modal-confirm-btn"
            :color="Theme.Danger"
            size="sm"
            class="w-full"
            @click="onModalConfirm"
            >{{ $t("account.withdrawal.button_continue") }}</DefaultButton
         >
      </template>
   </Modal>

   <!-- Withdrawal - step 2 - We value your feedback. Why are you withdrawing? -->
   <!-- Confirming this one will send a withdrawal API request to the backend. -->
   <UserStudyWithdrawalReasonsModal
      ref="modalStep2Ref"
      :key="studyId"
      :withdrawalReasonsResp="withdrawalReasonsResp"
      :studyId="studyId"
      :isOpen="step === 2"
      @end-withdrawal="endWithdrawalProcess"
      @go-back="onGoBack"
      @confirm="onModalConfirm"
   />

   <!-- Withdrawal - step 3 - You have successfully withdrawn from the study. -->
   <Modal
      v-if="step === 3"
      id="study-withdraw-step-3"
      ref="modalStep3Ref"
      width="sm"
      :pill="{ text: $t('account.withdrawal.modal_label'), theme: Theme.Green }"
      @close="endWithdrawalProcess"
   >
      <template #title>
         <h1 class="text-regular grow text-center text-xl text-black md:text-2xl">
            {{ $t("account.withdrawal.step3.heading") }}
         </h1>
      </template>
      <div data-cy="study-withdraw-step-3-modal-desc">
         <p class="mb-4 text-center">{{ $t("account.withdrawal.step3.text1") }}</p>
         <p class="text-center">{{ $t("account.withdrawal.step3.text2") }}</p>
      </div>
      <template #action-btns>
         <DefaultButton data-cy="test-modal-confirm-btn" size="sm" class="w-full max-w-[60%]" @click="onModalConfirm">{{
            $t("account.withdrawal.step3.button")
         }}</DefaultButton>
      </template>
   </Modal>
</template>

<script setup lang="ts">
import { Theme } from "@/types";
import type { WithdrawalStep } from "./UserStudies.vue";
import Modal from "@/components/ui/Modal.vue";
import StudiesAPI, { type StudyWithdrawalReason } from "@/data/api-layer/studiesApi";
import { useLocaleStore } from "@/stores/localeStore";
import type { AxiosError } from "axios";

const $t = useI18n().t;
const localeStore = useLocaleStore();

// Props
interface Props {
   step: WithdrawalStep;
   studyId: string;
   studyName: string;
}
const props = defineProps<Props>();

// Data
const withdrawalReasonsResp = ref<StudyWithdrawalReason[]>([]);

const loadErrors = ref<Error[]>([]);
interface State {
   isPageLoading: boolean;
}
const state = reactive<State>({
   isPageLoading: true,
});

// Emits
const emit = defineEmits(["on-withdrawal-close", "on-next-step", "on-previous-step"]);

// Modal related logic
//--------------------------------------------------------------------------------------------------
// We need to keep a reference to each modal to be able to close them using the closeModal function from the Modal component
const modalStep1Ref: Ref<InstanceType<typeof Modal>> = ref(null);
const modalStep2Ref: Ref<InstanceType<typeof Modal>> = ref(null);
const modalStep3Ref: Ref<InstanceType<typeof Modal>> = ref(null);

function callCloseModal() {
   // This methods use the closeModal() method exposed by the Modal component
   // We can use it by accessing the ref of the modal component
   if (props.step === 1) {
      modalStep1Ref.value.closeModal();
   } else if (props.step === 2) {
      modalStep2Ref.value.closeModal();
   } else if (props.step === 3) {
      modalStep3Ref.value.closeModal();
   }
}

function onGoBack() {
   emit("on-previous-step");
   callCloseModal();
}

function endWithdrawalProcess() {
   emit("on-withdrawal-close");
   callCloseModal();
}

async function onModalConfirm() {
   emit("on-next-step");
   callCloseModal();
}

// Fetching withdrawal reasons
//--------------------------------------------------------------------------------------------------
const fetchWithdrawalReasons = async () => {
   try {
      const response = await StudiesAPI.getStudyWithdrawalReasons();

      // Move "Other" and "Prefer not to say" to the end of the list
      const otherReason = response.find((reason) => reason.name === "Other");
      const preferNotToSayReason = response.find((reason) => reason.name === "Prefer not to say");

      if (otherReason) {
         response.splice(response.indexOf(otherReason), 1);
         response.push(otherReason);
      }

      if (preferNotToSayReason) {
         response.splice(response.indexOf(preferNotToSayReason), 1);
         response.push(preferNotToSayReason);
      }

      withdrawalReasonsResp.value = response;
   } catch (error) {
      console.error("Error fetching withdrawal data", error);
      loadErrors.value = [error as AxiosError];
   } finally {
      state.isPageLoading = false;
   }
};

onMounted(async () => {
   await fetchWithdrawalReasons();
});

// Withdrawal reasons are translated to all site languages
// So we need to refetch them when language changes
watch(
   () => localeStore.currentLocale,
   () => fetchWithdrawalReasons()
);
</script>
